import { FlowchartActionDto } from '../FlowchartActionDto';
import { FlowchartContainerDto } from '../FlowchartContainerDto';
import { FlowchartDecisionNodeDto } from '../FlowchartDecisionNodeDto';
import { FlowchartElementType } from '../FlowchartElementType';
import { FlowchartLinkAttributeOperator } from '../FlowchartLinkAttributeOperator';
import { FlowchartLinkDto } from '../FlowchartLinkDto';
import { FlowchartNodeDto, FlowchartNodeType } from '../FlowchartNodeDto';
import { FlowchartStartNodeDto } from '../FlowchartStartNodeDto';
import { FlowchartSubflowDto } from '../FlowchartSubflowDto';
import { FlowchartTextBlockDto } from '../FlowchartTextBlockDto';
import { GuidelineValidationResult } from '../GuidelineValidationResult';
import { Reference } from '../reference.model';
import { BaseEditableProperties, Widget } from './widget.model';

export type FlowchartWidget = Widget & {
  elements?: FlowchartNodeDto[];
  widgetType: 'flowchart';
} & {
  localChanges?: FlowchartWidgetLocalChanges;
};

export type FlowchartWidgetLocalChanges = BaseEditableProperties & {
  elements?: FlowchartNodeDto[];
};

export const createFlowchartWidget = (
  overrides: Partial<Omit<FlowchartWidget, 'getReferences' | 'widgetType' | 'validate'>>,
): FlowchartWidget => ({
  uniqueId: crypto.randomUUID(),
  attachments: [],
  links: [],
  references: [],
  elements: [],
  ...overrides,
  widgetType: 'flowchart',
});

export const flattenFlowchartElements = (widget: FlowchartWidget): FlowchartNodeType[] => {
  if (widget.widgetType !== 'flowchart' || !widget.elements) {
    return [];
  }

  const elements: FlowchartNodeType[] = [];
  const processElement = (element: FlowchartNodeType) => {
    elements.push(element);
    if (element.children && Array.isArray(element.children)) {
      element.children.forEach(processElement);
    }
  };

  widget.elements.forEach(processElement);
  return elements;
};

export const getReferencesFromFlowchartWidget = (widget: FlowchartWidget): Reference[] => {
  const widgetRefs = widget.references || [];

  const elementRefs: Reference[] = [];
  if (widget.elements) {
    const getReferencesFromFlowchart = (flowchartElements: FlowchartNodeDto[]): Reference[] => {
      const references: Reference[] = [];

      const collectElements = (elements: FlowchartNodeDto[]) => {
        for (const element of elements) {
          references.push(...(element.references ?? []));

          if (element.children?.length) {
            collectElements(element.children);
          }
        }
      };

      collectElements(flowchartElements);
      return references;
    };

    const flowchartReferences = getReferencesFromFlowchart(widget.elements);
    elementRefs.push(...flowchartReferences);
  }

  return [...widgetRefs, ...elementRefs];
};

export const validateFlowchartWidget = (widget: FlowchartWidget, params?: { chapter: { title: string } }): GuidelineValidationResult => {
  let isValid = false;
  isValid = validateFlowchartElements(widget.localChanges?.elements ?? widget.elements);

  if (!isValid) {
    return {
      valid: false,
      message: 'FlowchartChapterHasInvalidElements',
      params: { chapter: params.chapter.title },
    };
  }

  return {
    valid: true,
    message: '',
  };
};

export const validateFlowchartElements = (flowchartElements: FlowchartNodeDto[]): boolean => {
  for (const flowchartElement of flowchartElements) {
    if (flowchartElement.elementType == FlowchartElementType.FlowchartAction) {
      const flowchartAction = flowchartElement as FlowchartActionDto;

      if (!flowchartAction?.guidelineAction?.title) {
        return false;
      }
    } else if (flowchartElement.elementType == FlowchartElementType.FlowchartContainer) {
      const flowchartContainer = flowchartElement as FlowchartContainerDto;

      if (!flowchartContainer?.guidelineAction?.title) {
        return false;
      }
    } else if (flowchartElement.elementType == FlowchartElementType.FlowchartSubflow) {
      const flowchartSubflow = flowchartElement as FlowchartSubflowDto;
      if (!flowchartSubflow?.guidelineAction?.title) {
        return false;
      }
    } else if (flowchartElement.elementType == FlowchartElementType.FlowchartDecisionNode) {
      const flowchartDecision = flowchartElement as FlowchartDecisionNodeDto;
      if (!flowchartDecision.ruleTitle && !flowchartDecision?.guidelineCriteria?.title && !flowchartDecision?.guidelineObservation?.title) {
        return false;
      }
    } else if (flowchartElement.elementType == FlowchartElementType.FlowchartStartNode) {
      const flowchartStartNode = flowchartElement as FlowchartStartNodeDto;
      if (!flowchartStartNode?.guidelineAction?.title && !flowchartStartNode.guidelineCriteria?.title) {
        return false;
      }
    }

    if (flowchartElement.outgoingLinks?.length > 0) {
      const linksValid = validateFlowchartLinks(flowchartElement.outgoingLinks, flowchartElement, flowchartElements);
      if (!linksValid) {
        return false;
      }
    }

    if (flowchartElement.children?.length > 0) {
      const areChildrenValid = validateFlowchartElements(flowchartElement.children);
      if (!areChildrenValid) {
        return false;
      }
    }
  }

  return true;
};

function validateFlowchartLinks(
  flowchartLinks: FlowchartLinkDto[],
  fromFlowchartNode: FlowchartNodeDto,
  flowchartElements: FlowchartNodeDto[],
) {
  for (const flowchartLink of flowchartLinks) {
    if (fromFlowchartNode?.elementType === FlowchartElementType.FlowchartDecisionNode) {
      if (
        flowchartLink?.attribute?.operator === FlowchartLinkAttributeOperator.Between &&
        flowchartLink?.attribute?.value?.numericalValueRange?.item1 > flowchartLink?.attribute?.value?.numericalValueRange?.item2
      ) {
        return false;
      }
    }
    //check if lost link exists, still let user save but track error
    if (!flowchartElements.find((el) => el.id === flowchartLink.fromFlowchartNodeId)) {
      const error =
        'Invalid Link detected Link Id: ' +
        flowchartLink.id +
        ' Source node Missing ID: ' +
        flowchartLink.fromFlowchartNodeId +
        ' Target ID (existing): ' +
        flowchartLink.toFlowchartNodeId +
        ' Target Title and Code: ' +
        titleResolver(flowchartElements.find((el) => el.id === flowchartLink.toFlowchartNodeId));

      console.error(error);
    }
    if (!flowchartElements.find((el) => el.id === flowchartLink.toFlowchartNodeId)) {
      const error =
        'Invalid Link detected Link Id: ' +
        flowchartLink.id +
        ' Target node Missing ID: ' +
        flowchartLink.toFlowchartNodeId +
        ' Source ID (existing): ' +
        flowchartLink.fromFlowchartNodeId +
        ' Target Title and Code: ' +
        titleResolver(flowchartElements.find((el) => el.id === flowchartLink.fromFlowchartNodeId));

      console.error(error);
    }
  }

  return true;
}

function titleResolver(element: FlowchartNodeDto) {
  switch (element.elementType) {
    case FlowchartElementType.FlowchartAction:
      if ((element as FlowchartActionDto)?.guidelineAction) {
        return (
          (element as FlowchartActionDto)?.guidelineAction?.title +
          ' Code: ' +
          (element as FlowchartActionDto)?.guidelineAction?.activityDefinitionCode
        );
      } else {
        return (element as FlowchartActionDto)?.guidelineAction?.description;
      }
    case FlowchartElementType.FlowchartDecisionNode:
      if ((element as FlowchartDecisionNodeDto)?.guidelineCriteria) {
        return (
          (element as FlowchartDecisionNodeDto)?.guidelineCriteria?.title +
          ' Code: ' +
          (element as FlowchartDecisionNodeDto)?.guidelineCriteria?.findingDefinitionCode
        );
      }
      if ((element as FlowchartDecisionNodeDto)?.guidelineObservation) {
        return (
          (element as FlowchartDecisionNodeDto)?.guidelineObservation?.title +
          ' Code: ' +
          (element as FlowchartDecisionNodeDto)?.guidelineObservation?.observationDefinitionCode
        );
      }
      return (element as FlowchartActionDto)?.guidelineAction?.title;
    case FlowchartElementType.FlowchartSelectionBehavior:
      return ' Selection Behavior';
    case FlowchartElementType.FlowchartTextBlock:
      return ' Textblock Content' + (element as FlowchartTextBlockDto)?.content;
    case FlowchartElementType.FlowchartStartNode:
      if ((element as FlowchartStartNodeDto).guidelineCriteria) {
        return (
          ' Start Node: ' +
          (element as FlowchartStartNodeDto)?.guidelineCriteria?.title +
          ' Code: ' +
          (element as FlowchartStartNodeDto)?.guidelineCriteria?.findingDefinitionCode
        );
      }
      if ((element as FlowchartStartNodeDto)?.guidelineAction) {
        return (
          ' Start Node: ' +
          (element as FlowchartStartNodeDto)?.guidelineAction?.title +
          ' Code: ' +
          (element as FlowchartStartNodeDto)?.guidelineAction?.activityDefinitionCode
        );
      }
  }
}
